import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Wrench } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const emptyRow = (type = 'other_charge') => ({
  type, label: '', weight: '', rate: '', amount: '',
});

// One editable ledger row. Weight-based types compute amount = weight × rate.
const LedgerRow = ({ row, types, onChange, onRemove }) => {
  const meta = types.find((t) => t.key === row.type) || {};
  const isWeight = !!meta.weight_based;
  const computed = isWeight
    ? Math.round((parseFloat(row.weight) || 0) * (parseFloat(row.rate) || 0))
    : Math.round(parseFloat(row.amount) || 0);
  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid="repair-ledger-row">
      <select
        className="col-span-4 border rounded-md px-2 py-2 text-sm bg-white"
        value={row.type}
        onChange={(e) => onChange({ ...row, type: e.target.value })}
        data-testid="repair-row-type"
      >
        {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
      </select>
      {isWeight ? (
        <>
          <Input className="col-span-3" type="number" placeholder="wt (g)" value={row.weight}
            onChange={(e) => onChange({ ...row, weight: e.target.value })} data-testid="repair-row-weight" />
          <Input className="col-span-3" type="number" placeholder="rate/g" value={row.rate}
            onChange={(e) => onChange({ ...row, rate: e.target.value })} data-testid="repair-row-rate" />
        </>
      ) : (
        <Input className="col-span-6" type="number" placeholder="amount" value={row.amount}
          onChange={(e) => onChange({ ...row, amount: e.target.value })} data-testid="repair-row-amount" />
      )}
      <div className="col-span-1 text-right text-xs font-semibold text-gray-700">{inr(computed)}</div>
      <button className="col-span-1 text-red-500 hover:text-red-700 flex justify-end" onClick={onRemove} data-testid="repair-row-remove">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

/**
 * Reusable repair-estimate editor. Used both on an order detail (orderId set) and
 * standalone. `estimate` (optional) preloads an existing estimate for editing.
 */
export default function RepairEstimateDialog({ open, onOpenChange, orderId, orderNumber, leadId, sessionId, customerName, customerPhone, estimate, forceNew, reviseFrom, onSaved }) {
  const [chargeTypes, setChargeTypes] = useState([]);
  const [creditTypes, setCreditTypes] = useState([]);
  const [goldRate, setGoldRate] = useState('');
  const [gstPercent, setGstPercent] = useState(3);
  const [charges, setCharges] = useState([]);
  const [credits, setCredits] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [estId, setEstId] = useState(null);
  const [status, setStatus] = useState('draft');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [lt, gp] = await Promise.all([
          api.get('/repair-estimates/line-types'),
          api.get('/gold-prices/latest').catch(() => ({ data: { exists: false } })),
        ]);
        setChargeTypes(lt.data.charges || []);
        setCreditTypes(lt.data.credits || []);
        // Default gold rate = latest 22K (fallback: first available price)
        let rate = '';
        if (gp.data?.exists && gp.data.prices) {
          rate = gp.data.prices['22K'] || Object.values(gp.data.prices)[0] || '';
        }
        setGoldRate(estimate?.gold_rate || rate || '');
      } catch (e) { /* noop */ }
    })();
  }, [open]);

  // Load estimate data (edit mode / revise clone) or reset (new)
  useEffect(() => {
    if (!open) return;
    if (estimate) {
      // forceNew (revise): prefill from the source but save as a brand-new draft
      setEstId(forceNew ? null : estimate.id);
      setStatus(forceNew ? 'draft' : (estimate.status || 'draft'));
      setGstPercent(estimate.gst_percent ?? 3);
      setNotes(estimate.notes || '');
      setCharges((estimate.charges || []).map((c) => ({ type: c.type, label: c.label || '', weight: c.weight ?? '', rate: c.rate ?? '', amount: c.amount ?? '' })));
      setCredits((estimate.credits || []).map((c) => ({ type: c.type, label: c.label || '', weight: c.weight ?? '', rate: c.rate ?? '', amount: c.amount ?? '' })));
    } else {
      setEstId(null); setStatus('draft'); setGstPercent(3); setNotes('');
      setCharges([emptyRow('making')]); setCredits([]);
    }
  }, [open, estimate, forceNew]);

  const rowAmount = (r, types) => {
    const meta = types.find((t) => t.key === r.type) || {};
    return meta.weight_based
      ? Math.round((parseFloat(r.weight) || 0) * (parseFloat(r.rate) || 0))
      : Math.round(parseFloat(r.amount) || 0);
  };

  const totals = useMemo(() => {
    const sc = charges.reduce((s, r) => s + rowAmount(r, chargeTypes), 0);
    const cc = credits.reduce((s, r) => s + rowAmount(r, creditTypes), 0);
    // GST applies on all charges first, then credits are deducted from the GST-inclusive total.
    const gst = Math.round(sc * (parseFloat(gstPercent) || 0) / 100);
    const chargesWithGst = sc + gst;
    const net = chargesWithGst - cc;
    return { sc, cc, gst, chargesWithGst, net, customerPays: net >= 0 };
  }, [charges, credits, chargeTypes, creditTypes, gstPercent]);

  const buildPayload = () => ({
    order_id: orderId || null,
    lead_id: leadId || null,
    session_id: sessionId || null,
    customer_name: customerName || null,
    customer_phone: customerPhone || null,
    revised_from: forceNew ? (reviseFrom || null) : null,
    gold_rate: parseFloat(goldRate) || null,
    gst_percent: parseFloat(gstPercent) || 0,
    charges: charges.map((r) => ({ type: r.type, label: r.label || null, weight: r.weight ? parseFloat(r.weight) : null, rate: r.rate ? parseFloat(r.rate) : null, amount: r.amount ? parseFloat(r.amount) : null })),
    credits: credits.map((r) => ({ type: r.type, label: r.label || null, weight: r.weight ? parseFloat(r.weight) : null, rate: r.rate ? parseFloat(r.rate) : null, amount: r.amount ? parseFloat(r.amount) : null })),
    notes: notes || null,
  });

  const save = async (thenConfirm = false) => {
    if (charges.length === 0 && credits.length === 0) { toast.error('Add at least one charge or credit line'); return; }
    setSaving(true);
    try {
      let saved;
      if (estId) {
        saved = (await api.put(`/repair-estimates/${estId}`, buildPayload())).data;
      } else {
        saved = (await api.post('/repair-estimates', buildPayload())).data;
        setEstId(saved.id);
      }
      if (thenConfirm) {
        saved = (await api.post(`/repair-estimates/${saved.id}/confirm`)).data;
        toast.success('Repair estimate confirmed');
      } else {
        toast.success('Repair estimate saved');
      }
      setStatus(saved.status);
      onSaved?.(saved);
      if (thenConfirm) onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const isLocked = status !== 'draft';
  const statusLabel = status === 'active' ? 'Active' : status === 'superseded' ? 'Superseded' : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="repair-estimate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-teal-600" />
            Repair Estimate {orderNumber ? `— ${orderNumber}` : ''}
            {statusLabel && <span className={`text-xs px-2 py-0.5 rounded-full ${status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{statusLabel}</span>}
          </DialogTitle>
        </DialogHeader>

        {isLocked && (
          <div className="bg-gray-50 border border-gray-200 text-gray-600 text-xs rounded-md p-2" data-testid="repair-locked-note">
            {status === 'active'
              ? 'This is the active estimate feeding the order price. Reopen to edit, or use “Revise at today’s rate” to make a new version.'
              : 'This estimate has been superseded by a newer version. Reopen to edit if needed.'}
          </div>
        )}

        <fieldset disabled={isLocked} className="space-y-5">
          {/* Gold rate + GST */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gold rate (₹/g)</Label>
              <Input type="number" value={goldRate} onChange={(e) => setGoldRate(e.target.value)} data-testid="repair-gold-rate" />
            </div>
            <div>
              <Label>GST %</Label>
              <Input type="number" value={gstPercent} onChange={(e) => setGstPercent(e.target.value)} data-testid="repair-gst-percent" />
            </div>
          </div>

          {/* CHARGES */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-emerald-700">Charges (customer pays)</h4>
              <Button size="sm" variant="outline" onClick={() => setCharges([...charges, emptyRow('other_charge')])} data-testid="add-charge-row">
                <Plus className="w-4 h-4 mr-1" /> Add charge
              </Button>
            </div>
            <div className="space-y-2">
              {charges.map((r, i) => (
                <LedgerRow key={i} row={r} types={chargeTypes}
                  onChange={(nr) => setCharges(charges.map((x, j) => (j === i ? nr : x)))}
                  onRemove={() => setCharges(charges.filter((_, j) => j !== i))} />
              ))}
              {charges.length === 0 && <p className="text-xs text-gray-400">No charges added.</p>}
            </div>
          </div>

          {/* CREDITS */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-amber-700">Credits (we pay / adjust)</h4>
              <Button size="sm" variant="outline" onClick={() => setCredits([...credits, emptyRow('old_gold')])} data-testid="add-credit-row">
                <Plus className="w-4 h-4 mr-1" /> Add credit
              </Button>
            </div>
            <div className="space-y-2">
              {credits.map((r, i) => (
                <LedgerRow key={i} row={r} types={creditTypes}
                  onChange={(nr) => setCredits(credits.map((x, j) => (j === i ? nr : x)))}
                  onRemove={() => setCredits(credits.filter((_, j) => j !== i))} />
              ))}
              {credits.length === 0 && <p className="text-xs text-gray-400">No credits added.</p>}
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)} data-testid="repair-notes" />
          </div>
        </fieldset>

        {/* Totals */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm" data-testid="repair-totals">
          <div className="flex justify-between text-gray-600"><span>Total charges</span><span>{inr(totals.sc)}</span></div>
          <div className="flex justify-between text-gray-600"><span>GST ({gstPercent}%) on charges</span><span>+ {inr(totals.gst)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Charges incl. GST</span><span>{inr(totals.chargesWithGst)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Total credits</span><span>− {inr(totals.cc)}</span></div>
          <div className={`flex justify-between font-bold text-base pt-1 border-t ${totals.customerPays ? 'text-emerald-700' : 'text-red-600'}`} data-testid="repair-net-banner">
            <span>{totals.customerPays ? 'Customer pays' : 'We refund'}</span>
            <span>{inr(Math.abs(totals.net))}</span>
          </div>
        </div>

        {!isLocked && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-repair-estimate">
              {saving ? 'Saving…' : 'Save draft'}
            </Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => save(true)} disabled={saving} data-testid="confirm-repair-estimate">
              {saving ? 'Saving…' : 'Confirm estimate'}
            </Button>
          </div>
        )}
        {isLocked && (
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-repair-estimate">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
